import React from 'react';
import * as styles from './PostViewError.module.scss';
import useRequestPostAccess from './useRequestPostAccess';
import Button, { ButtonWithLoadingState } from '../components/Button';
import SlabLogo from '../components/SlabLogo';
import postPath from '../utils/url/postPath';

interface PostViewErrorContainerProps {
  primaryButtonTo?: string;
  primaryButtonOnClick?: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonClassName?: string;
  primaryButtonLabel: string;
  primaryButtonLoading?: boolean;
  secondaryButtonTo: string;
  secondaryButtonLabel: string;
  title: string;
  message: string;
}

const PostViewErrorContainer = ({
  secondaryButtonTo,
  secondaryButtonLabel,
  primaryButtonTo,
  primaryButtonDisabled,
  primaryButtonClassName,
  primaryButtonOnClick,
  primaryButtonLabel,
  primaryButtonLoading,
  title,
  message,
}: PostViewErrorContainerProps) => (
  <div className={styles.container}>
    <div className={styles.background} />
    <div className={styles.inner}>
      <div className={styles.content}>
        <SlabLogo className={styles.logo} />
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.message}>{message}</div>
        <div className={styles.buttons}>
          <Button
            to={secondaryButtonTo}
            target="_blank"
            size="large"
            variant="secondary"
            rel="noreferrer"
          >
            {secondaryButtonLabel}
          </Button>
          <ButtonWithLoadingState
            to={primaryButtonTo}
            size="large"
            variant="primary"
            onClick={primaryButtonOnClick}
            disabled={primaryButtonDisabled}
            className={primaryButtonClassName}
            loading={primaryButtonLoading ?? false}
          >
            {primaryButtonLabel}
          </ButtonWithLoadingState>
        </div>
      </div>
    </div>
  </div>
);

const embedLogin = () => {
  window.open('/embed/login', '_blank', 'width=480,height=600');
};

export const AllowAccessPostViewError = () => (
  <PostViewErrorContainer
    title="Can’t access your Slab account"
    message="Your browser restricts access when Slab is embedded. Allow access to log you into your account."
    secondaryButtonTo="https://go.slab.com/embed-browser-storage"
    secondaryButtonLabel="Learn more"
    primaryButtonLabel="Allow access"
    primaryButtonOnClick={embedLogin}
  />
);

interface RequestAccessPostViewErrorProps {
  postId: string;
}

export const RequestAccessPostViewError = ({
  postId,
}: RequestAccessPostViewErrorProps) => {
  const postLink = postPath({ title: '', id: postId });

  const { requestAccess, hasRequestedAccess, loading } = useRequestPostAccess({
    postId,
  });

  return (
    <PostViewErrorContainer
      title="Can’t show this Slab post"
      message="You may not have access to this post or try opening in a new tab to make sure it exists."
      secondaryButtonLabel="Open tab"
      secondaryButtonTo={postLink}
      primaryButtonDisabled={hasRequestedAccess}
      primaryButtonClassName={styles.requestAccessButton}
      primaryButtonLabel={hasRequestedAccess ? 'Requested' : 'Request access'}
      primaryButtonOnClick={() => requestAccess()}
      primaryButtonLoading={loading}
    />
  );
};

const isInIframe = () => window.self !== window.top;

interface NotLoggedInPostViewErrorProps {
  postId: string;
}

export const NotLoggedInPostViewError = ({
  postId,
}: NotLoggedInPostViewErrorProps) => (
  <PostViewErrorContainer
    title="Can’t show this Slab post"
    message="You may need to log in to view this post or ask the owner to make it publicly viewable."
    secondaryButtonLabel="Open tab"
    secondaryButtonTo={postPath({ title: '', id: postId })}
    primaryButtonLabel="Log in to Slab"
    {...(isInIframe()
      ? {
          primaryButtonOnClick: embedLogin,
        }
      : {
          primaryButtonTo: `/login?redirect=${encodeURIComponent(
            window.location.pathname,
          )}`,
        })}
  />
);
